import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { Card, CardContent } from "@babackapp/ui/components/card";
import useUpdatePortalExchange from "@babackapp/private-api-client/client/use-update-portal-exchange";
import PortalExchangeForm from "./portal-exchange-form";

import { z } from "zod";

export const PortalExchangeFormSchema = z.object({
  activated: z.boolean(),
  exchangeOrderAt0: z.boolean(),
  exchangeWithOtherArticles: z.boolean(),
  exchangeWithOtherArticlesPaymentButtonQuerySelector: z.string(),
  maxDifference: z.number(),
  minimalStock: z.number(),
  reserveInventory: z.boolean(),
  reshippingHasToBePaid: z.boolean(),
  tagsForNewOrder: z.array(z.string()),
  tagsForNotExchangeOrder: z.array(z.string()),
  collections: z.array(
    z.object({
      collection: z.string(),
      optionTitle: z.string(),
    }),
  ),
  shippingLineForExchange: z
    .object({
      code: z.string(),
      title: z.string(),
    })
    .optional(),
  shippingMethods: z.array(
    z.object({
      code: z.string().optional(),
      isPickingPoint: z.boolean(),
      title: z.string(),
      currencyCode: z.string().optional().nullable(),
      originalAmount: z.string().optional().nullable(),
      carrierIdentifier: z.string().optional().nullable(),
      exchangeOverride: z
        .object({
          code: z.string(),
          title: z.string(),
        })
        .optional()
        .nullable(),
    }),
  ),
  exchangeAcceptedCountries: z.array(z.string()).optional(),
  exchangeCreditAcceptedCountries: z.array(z.string()).optional(),
  tagsForNotExchangeCreditOrder: z.array(z.string()),
  customAttributeKeysToForward: z.array(z.string()).optional(),
  exchangeCreditRemainingMethod: z
    .enum(["customer_choice", "discount", "refund"])
    .nullable()
    .optional(),
  stockLocationId: z.string().nullable().optional(),
});

export type PortalExchangeFormValues = z.infer<typeof PortalExchangeFormSchema>;

export default function PortalExchange() {
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updatePortalExchange } = useUpdatePortalExchange(shop);

  const onSubmit = (values: PortalExchangeFormValues) => {
    return updatePortalExchange(values);
  };

  return (
    <Card className="w-full">
      <CardContent>
        <PortalExchangeForm
          onSubmit={onSubmit}
          defaultValues={{
            activated: shop.exchange.activated,
            exchangeOrderAt0: shop.exchange.exchangeOrderAt0,
            reserveInventory: shop.exchange.reserveInventory,
            maxDifference: shop.exchange.maxDifference,
            minimalStock: shop.exchange.minimalStock,
            tagsForNewOrder: shop.exchange.tagsForNewOrder,
            tagsForNotExchangeOrder: shop.exchange.tagsForNotExchangeOrder,
            reshippingHasToBePaid: shop.exchange.reshippingHasToBePaid,
            exchangeAcceptedCountries: shop.exchange.exchangeAcceptedCountries,
            exchangeCreditAcceptedCountries:
              shop.exchange.exchangeCreditAcceptedCountries,
            tagsForNotExchangeCreditOrder:
              shop.exchange.tagsForNotExchangeCreditOrder ?? [],
            customAttributeKeysToForward:
              shop.exchange.customAttributeKeysToForward,
            exchangeWithOtherArticles: shop.exchange.exchangeWithOtherArticles,
            exchangeWithOtherArticlesPaymentButtonQuerySelector:
              shop.exchange.exchangeWithOtherArticlesPaymentButtonQuerySelector,
            collections: shop.exchange.collections,
            shippingMethods: shop.exchange.shippingMethods,
            exchangeCreditRemainingMethod:
              shop.exchange.exchangeCreditRemainingMethod || "customer_choice",
            stockLocationId: shop.exchange.stockLocationId ?? null,
          }}
        />
      </CardContent>
    </Card>
  );
}
